import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { ToastTypes, launchToast } from '../../components'
import { removeKey, storeSelected, syncSelected, useFormSaveStore, useTabTitle } from '../../utils'
import { StationProvider } from './StationContext'
import { StationView } from './StationView'
import { StationData, StationDirections, StationName, TrajectoryConfigurationMode } from './station_types'

import { useGetStationByParkingLot } from '../Lot/utils/lot-hooks'
import {
    useDeleteStation,
    useGetDeviceData,
    useGetDeviceUuids,
    useGetStationByDeviceId,
    useGetStationDataByName,
    useGetStationNames,
    useSaveODDisplayOverride,
    useSaveStation,
    useSaveStationConfig,
    useSaveStationConfigFilter,
    useUpdateStationEnabled
} from './utils/station-hooks'
import {
    getLiveViewAccessToken,
    getLiveViewObject,
    getLiveViewUrl,
    useUpdateDeviceConfigMutation
} from '../DeviceConfig/utils'
import { useLiveViewStateStore } from '../LiveView/LiveViewStore'
import { browserHistory } from '../../App'
import { ODDisplayOverrideConfig } from './ODDisplayOverride'
import { LotStationType } from '../Lot/lot-types'
export const StationViewContainer = () => {
    const location = useLocation()
    const queryParams = new URLSearchParams(location.search)
    const [selectedStation, setSelectedStationState] = useState<StationName | null | undefined>()

    let stationName = queryParams.get('stationName')
    const storedSelectedStation = syncSelected('stationName')
    const history = useHistory()
    if (!stationName) {
        if (storedSelectedStation) {
            history.push(`?stationName=${storedSelectedStation}`)
            stationName = storedSelectedStation
        }
    } else {
        if (storedSelectedStation && storedSelectedStation !== stationName) {
            storeSelected('stationName', stationName)
        }
    }

    const queryClient = useQueryClient()

    const [configurationMode, setConfigurationModeState] = useState<TrajectoryConfigurationMode>(
        TrajectoryConfigurationMode.FilterTool
    )
    const { data, refreshStationNames, loadingFetchStationNames } = useGetStationNames()
    useTabTitle(selectedStation, (station) => station?.station_name || 'Station')
    const { data: deviceList } = useGetDeviceUuids()
    const {
        data: selectedStationData,
        refetch: refreshSelectedStationData,
        loading: loadingStationData
    } = useGetStationDataByName(selectedStation?.station_name)
    const { data: deviceStations, refetchDeviceStations } = useGetStationByDeviceId(
        selectedStationData?.station[0]?.device_uuid
    )
    const { data: deviceData, refetch: refetchDeviceData } = useGetDeviceData(
        selectedStationData?.station[0]?.device_uuid
    )
    const { setBaseURL, setIsPmView, setLiveViewObject, setStationName } = useLiveViewStateStore()

    useEffect(() => {
        if (deviceData) {
            setBaseURL(getLiveViewUrl(deviceData.device[0]) || 'FAILURE')
            setIsPmView(true)
            setLiveViewObject(getLiveViewObject(deviceData.device[0]))

            setStationName(stationName || null)
            localStorage.setItem('liveViewAccessToken', getLiveViewAccessToken(deviceData.device[0]) || 'FAILURE')
        }
    }, [deviceData])

    const { execute: deleteStation, isDeletingStation } = useDeleteStation(() => {
        history.push(`/stationName`)
        removeKey('stationName')
        queryClient.clear()
        setSelectedStationState(null)
        refreshStationNames()
    })
    const { saveStationConfigFilter, loadingSaveStationConfigFilter } = useSaveStationConfigFilter(
        refreshSelectedStationData,
        selectedStation
    )
    const { saveODDisplayOverride, loadingSaveODDisplayOverride, TriggerScsFetchOccupancy } = useSaveODDisplayOverride(
        refreshSelectedStationData,
        selectedStation
    )

    const { saveStationConfig, loadingSaveStationConfig } = useSaveStationConfig(
        refreshSelectedStationData,
        selectedStation
    )
    const { saveStation, loadingSaveStation } = useSaveStation(refreshSelectedStationData, selectedStation)
    const { setStationEnabled, loadingUpdateStationEnabled } = useUpdateStationEnabled(
        refreshSelectedStationData,
        selectedStation
    )
    const { data: stationsInLot } = useGetStationByParkingLot(selectedStationData?.station[0]?.lot_section_id)
    const { updateDeviceConfig, loading: loadingDeviceConfigSave } = useUpdateDeviceConfigMutation()

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const saveDiffConfigHandler = async (diff_config: any): Promise<void> => {
        if (!deviceData?.device[0].uuid) {
            return
        }
        try {
            await updateDeviceConfig({
                device_uuid: deviceData.device[0].uuid,
                diff_config,
                timestamp: deviceData.device[0].config_target_timestamp ?? ''
            })
            refetchDeviceData()
            launchToast(ToastTypes.Success, 'Successfully updated config!')
        } catch (error) {
            launchToast(ToastTypes.Error, 'Error: Could not save config. Check the console for more details !')
            console.error(error)
        }
    }

    useEffect(() => {
        if (selectedStation) {
            refreshSelectedStationData()
        }
    }, [selectedStation, refreshSelectedStationData])

    useEffect(() => {
        const deviceUuid = selectedStationData?.station[0]?.device_uuid
        if (deviceUuid) {
            refetchDeviceStations()
            refetchDeviceData()
        }
    }, [selectedStationData, refetchDeviceStations, refetchDeviceData])

    useEffect(() => {
        if (stationName && data?.station) {
            const foundDevice = data.station.find((station: StationName) => station.station_name === stationName)
            if (foundDevice) {
                setSelectedStationState(foundDevice)
            } else {
                launchToast(ToastTypes.Error, 'Cannot find The device.')
            }
        }
    }, [stationName, data])

    const setSelectedStation = (station: StationName | null | undefined): void => {
        const { hasChanges, resetChanges } = useFormSaveStore.getState()

        if (hasChanges) {
            const confirmed = window.confirm('You have unsaved changes. Are you sure you want to switch devices?')
            if (!confirmed) return
            const unblock = browserHistory.block(true)

            setTimeout(() => unblock(), 0)
            resetChanges()
        }
        if (station) {
            storeSelected('stationName', station.station_name)
            history.push(`?stationName=${station.station_name}`)
        } else {
            removeKey('stationName')
            history.push(`/stationName`)
        }
        setSelectedStationState(station)
    }

    const saveStationConfigHandler = (enabledAutoWhitelisting: boolean, numberOfDays: number | null) => {
        saveStationConfig(
            enabledAutoWhitelisting,
            numberOfDays,
            selectedStation,
            selectedStationData?.station[0] as LotStationType
        )
    }

    const saveODDisplayOverrideHandler = (newODConfig: ODDisplayOverrideConfig) => {
        saveODDisplayOverride(newODConfig, selectedStation, selectedStationData)
        const lotPrefix = selectedStation?.station_name.split('_')[0]
        if (lotPrefix) {
            TriggerScsFetchOccupancy({ force_update_lot: lotPrefix })
        }
    }

    const saveStationConfigFilterHandler = (newFilter: any) => {
        saveStationConfigFilter(newFilter, selectedStation, selectedStationData)
    }

    const saveStationHandler = (device_direction: StationDirections, device_uuid?: string | null | undefined) => {
        saveStation(device_direction, selectedStation, selectedStationData?.station[0]?.lot_section_id, device_uuid)
    }

    const deleteStationHandler = () => {
        if (selectedStation?.station_name === undefined) {
            launchToast(ToastTypes.Error, 'Error: Could not delete the station. No Station Selected !')
            return
        }
        deleteStation(selectedStation?.station_name)
    }

    const getLotId = () => {
        return selectedStation?.station_name.split('_')[0]
    }
    return (
        <StationProvider
            context={{
                devices: deviceList?.device || [],
                stations: data?.station,
                loadingStations: loadingFetchStationNames,
                loadingStationData,
                loadingSaveStationConfig,
                setSelectedStation,
                selectedStation,
                selectedStationDeviceStations: deviceStations?.station,
                selectedStationData: selectedStationData?.station[0] as StationData,
                info: {
                    deviceName: deviceData?.device[0].device_name
                },
                deviceData: deviceData?.device[0],
                deleteStation: deleteStationHandler,
                isDeletingStation,
                saveStation: saveStationHandler,
                loadingSaveStation,
                setStationEnabled,
                loadingUpdateStationEnabled,
                saveStationConfig: saveStationConfigHandler,
                saveStationConfigFilter: saveStationConfigFilterHandler,
                saveODDisplayOverrideHandler,
                loadingSaveODDisplayOverride,
                saveDiffConfig: saveDiffConfigHandler,
                configurationMode,
                setConfigurationMode: setConfigurationModeState,
                loadingSaveStationConfigFilter: loadingSaveStationConfigFilter,
                loadingDeviceConfigSave: loadingDeviceConfigSave,
                stationsInLot,
                getLotId
            }}
        >
            <StationView />
        </StationProvider>
    )
}
